import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BLOCK_INSET_HEIGHT_PX,
  BLOCK_INSET_TOP_PX,
  CourtColumnModel,
  CourtColumnSegment,
} from '../../court-grid.model';
import { hasOverlapWithBlockedRanges } from '../../court-grid-blocked-ranges.util';
import { minutesBetween } from '../../court-grid-time.util';
import {
  isCourseClickable,
  segmentDisplayText,
  segmentKindClass,
  segmentLabel,
  segmentTitle,
} from '../../court-segment-label.util';
import { VenueTimezoneService } from '../../../../../service/venue-timezone.service';
import { AggregatedCourseSessionDto } from '../../../../../service/court-availability-service';

/** A single interval-aligned, clickable free-slot cell — derived on the fly, never stored. */
interface FreeCellVm {
  startTimeUtc: string;
  top: number;
  height: number;
  outOfLeadTime: boolean;
}

/** Payload emitted when the user activates a free cell (click/tap or keyboard). */
export interface FreeCellActivatedEvent {
  dateStr: string;
  courtId: string;
  courtName: string;
  startTimeUtc: string;
}

/** Payload emitted on mouse-pointerdown on a free cell — starts a
 *  press-drag-release selection gesture (root owns the drag session). */
export interface FreeCellDragStartEvent extends FreeCellActivatedEvent {
  pointerEvent: PointerEvent;
}

/**
 * Dumb leaf: renders one court's vertical column for one day — derived
 * free-cell click targets plus merged occupied/course/past segment blocks.
 * Owns no state beyond pure per-input derivations; the selection overlay
 * itself is rendered by the root component as a sibling, not by this column.
 */
@Component({
  selector: 'app-court-column',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './court-column.component.html',
  styleUrls: ['./court-column.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourtColumnComponent {
  column = input.required<CourtColumnModel>();
  /** YYYY-MM-DD calendar date this column belongs to (day-level, not on CourtColumnModel itself). */
  dateStr = input.required<string>();
  pxPerMinute = input.required<number>();
  intervalMinutes = input.required<number>();
  courtColumnWidth = input<number>(90);
  /** Cells starting after this instant render greyed-out and are not selectable. null = no limit. */
  maxBookableUtc = input<Date | null>(null);

  /** Emitted for every free-cell activation — the root decides validity (lead-time gating, etc). */
  freeCellActivated = output<FreeCellActivatedEvent>();

  /** Emitted on mouse-pointerdown on a free cell — the root starts a
   *  press-drag-release create gesture (drag down = longer duration). */
  freeCellDragStarted = output<FreeCellDragStartEvent>();

  /**
   * Emitted when the user activates a clickable course block (click/Enter/
   * Space) — bubbled up unchanged to booking-container.onCourseSelected(),
   * which opens the shared course-enrollment modal. Only fires for course
   * segments that are not past and where the user is NOT already enrolled
   * (see isCourseClickable()).
   */
  courseSessionClicked = output<AggregatedCourseSessionDto>();

  /** Hit-test anchor for drag geometry resolution via document.elementFromPoint(). */
  gridColKey = computed(() => `${this.dateStr()}__${this.column().courtId}`);

  totalHeightPx = computed(() => {
    const col = this.column();
    if (!col.dayStartUtc || !col.dayEndUtc) return 0;
    return minutesBetween(col.dayStartUtc, col.dayEndUtc) * this.pxPerMinute();
  });

  /**
   * Hour/half-hour gridline stripes, computed from the live pxPerMinute()
   * and intervalMinutes() rather than hardcoded — a fixed hour-height CSS
   * background silently desyncs from the actual cell geometry on mobile
   * (MOBILE_HOUR_H < HOUR_H in the root component) and for any category
   * configured with a non-30-minute interval.
   */
  gridlineBackground = computed(() => {
    const pxPerMin = this.pxPerMinute();
    const hourPx = Math.max(1, Math.round(pxPerMin * 60));
    const intervalPx = Math.max(1, Math.round(pxPerMin * this.intervalMinutes()));
    return (
      `repeating-linear-gradient(#ffffff, #ffffff ${hourPx - 1}px, #e4e9f0 ${hourPx - 1}px, #e4e9f0 ${hourPx}px), ` +
      `repeating-linear-gradient(#ffffff, #ffffff ${intervalPx - 1}px, #f2f4f7 ${intervalPx - 1}px, #f2f4f7 ${intervalPx}px)`
    );
  });

  segments = computed(() => this.column().segments.filter(s => s.kind !== 'available'));

  freeCells = computed<FreeCellVm[]>(() => {
    const col = this.column();
    if (!col.dayStartUtc || !col.dayEndUtc) return [];

    const interval = this.intervalMinutes();
    const pxPerMin = this.pxPerMinute();
    const maxBookable = this.maxBookableUtc();
    const startMs = new Date(col.dayStartUtc).getTime();
    const endMs = new Date(col.dayEndUtc).getTime();
    const stepMs = interval * 60_000;

    const cells: FreeCellVm[] = [];
    for (let t = startMs; t + stepMs <= endMs; t += stepMs) {
      if (hasOverlapWithBlockedRanges(t, t + stepMs, col.blockedRanges)) continue;
      cells.push({
        startTimeUtc: new Date(t).toISOString(),
        top: ((t - startMs) / 60_000) * pxPerMin,
        height: interval * pxPerMin,
        outOfLeadTime: maxBookable != null && t > maxBookable.getTime(),
      });
    }
    return cells;
  });

  onFreeCellActivate(cell: FreeCellVm): void {
    this.freeCellActivated.emit({
      dateStr: this.dateStr(),
      courtId: this.column().courtId,
      courtName: this.column().courtName,
      startTimeUtc: cell.startTimeUtc,
    });
  }

  /**
   * Mouse only: on touch, pointerdown must stay free so the browser can pan
   * the scrollbox (free cells cover most of the grid and have no
   * touch-action:none) — touch users tap to select via the (click) handler
   * and adjust duration with the overlay's resize handles. Out-of-lead-time
   * cells never start a gesture (matches the click path's gating).
   */
  onFreeCellPointerDown(event: PointerEvent, cell: FreeCellVm): void {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (cell.outOfLeadTime) return;
    this.freeCellDragStarted.emit({
      dateStr: this.dateStr(),
      courtId: this.column().courtId,
      courtName: this.column().courtName,
      startTimeUtc: cell.startTimeUtc,
      pointerEvent: event,
    });
  }

  freeCellAriaLabel(cell: FreeCellVm): string {
    const time = this.formatTime(cell.startTimeUtc);
    if (cell.outOfLeadTime) {
      return `${this.column().courtName}, ${time}, außerhalb der Vorlaufzeit`;
    }
    return `${this.column().courtName}, ${time}, frei`;
  }

  /** Un-inset segment top — the coordinate-space value (minutes × pxPerMinute). */
  segTop(seg: CourtColumnSegment): number {
    return minutesBetween(this.column().dayStartUtc, seg.startTimeUtc) * this.pxPerMinute();
  }

  /** Un-inset segment height — also drives segmentTier(), which must see the
   *  full geometry (tier thresholds match the mockup's pre-inset `h`). */
  segHeight(seg: CourtColumnSegment): number {
    return minutesBetween(seg.startTimeUtc, seg.endTimeUtc) * this.pxPerMinute();
  }

  /** Rendered block top: +1px visual inset (see BLOCK_INSET_TOP_PX). */
  segBlockTop(seg: CourtColumnSegment): number {
    return this.segTop(seg) + BLOCK_INSET_TOP_PX;
  }

  /** Rendered block height: -2px visual inset so back-to-back blocks don't touch. */
  segBlockHeight(fullHeight: number): number {
    return fullHeight - BLOCK_INSET_HEIGHT_PX;
  }

  /** Shared with the lanes column — see court-segment-label.util. */
  segmentKindClass(seg: CourtColumnSegment): string {
    return segmentKindClass(seg);
  }

  segmentTier(heightPx: number): 'xs' | 'sm' | 'lg' {
    if (heightPx < 34) return 'xs';
    if (heightPx < 58) return 'sm';
    return 'lg';
  }

  /** Plan §6.4.3 type word ("Kurs" / "Turnier – X" / "Gesperrt" / "Belegt"). */
  segmentLabel(seg: CourtColumnSegment): string {
    return segmentLabel(seg);
  }

  /** The line rendered inside the block (adds "· Anmelden · 12/16" on a joinable event). */
  segmentText(seg: CourtColumnSegment): string {
    return segmentDisplayText(seg);
  }

  segmentTitle(seg: CourtColumnSegment): string {
    return segmentTitle(
      seg,
      this.column().courtName,
      this.formatRange(seg.startTimeUtc, seg.endTimeUtc),
    );
  }

  /** Accessible label for the "Ecke" own-booking corner badge. */
  ownBadgeLabel(seg: CourtColumnSegment): string {
    return seg.kind === 'course' ? 'Du bist angemeldet' : 'Deine Buchung';
  }

  /**
   * A course/event block is clickable (opens the enrollment modal); a
   * 'blocked' Platzsperre never is. See court-segment-label.util.
   */
  isCourseClickable(seg: CourtColumnSegment): boolean {
    return isCourseClickable(seg);
  }

  onCourseSegmentActivate(seg: CourtColumnSegment): void {
    if (!this.isCourseClickable(seg)) return;
    this.courseSessionClicked.emit(seg.courseSession!);
  }

  courseAriaLabel(seg: CourtColumnSegment): string {
    return `${this.segmentTitle(seg)} · Details & Anmeldung`;
  }

  formatRange(startTimeUtc: string, endTimeUtc: string): string {
    return `${this.formatTime(startTimeUtc)}–${this.formatTime(endTimeUtc)}`;
  }

  formatTime(isoInstant: string): string {
    try {
      return this.venueTimezone.formatTime(isoInstant);
    } catch {
      return '';
    }
  }


  constructor(private venueTimezone: VenueTimezoneService) {}
}
